// Mintage Pay's payment page in a pop-up over this page instead of leaving the site.
// Ported from the Mintage Pay custom-store kit (kit/custom-store-popup/openMintagePayPopup.ts, the same pop-up as the
// Mintage Pay WooCommerce plugin 1.20.0): TypeScript annotations removed, logic unchanged. The order is marked paid by
// the signed webhook on our server, never here.

/** The buyer's details from the order, so the card form does not ask for them again (sent to Mintage's origin only). */
export function buyerFromOrder(order, email) {
  const a = (order && order.shipping_address) || {};
  return {
    email: email || order?.email || undefined,
    name: a.name || undefined,
    phone: a.phone || undefined,
    line1: a.line1 || undefined,
    city: a.city || undefined,
    state: a.province || undefined,
    postal: a.postal_code || undefined,
    country: "CA",
  };
}

/**
 * Opens the Mintage payment page in a secure pop-up. `buyer`, when given, is posted to the payment page when it says
 * it is ready. If the page has not come up in the frame within a few seconds, the buyer is sent to it instead.
 * After payment the buyer lands on `returnPath`, which shows the order's status.
 */
export function openMintagePayPopup(url, returnPath, buyer) {
  let origin = "";
  let invoice = "";
  try {
    const u = new URL(url);
    origin = u.origin;
    invoice = u.pathname.split("/")[2] ?? "";
  } catch {
    invoice = "";
  }
  if (!origin || !invoice || typeof window.postMessage !== "function") {
    window.location.href = url;
    return;
  }
  if (document.getElementById("mintagepay-popup")) return;

  if (!document.getElementById("mintagepay-popup-css")) {
    const css = document.createElement("style");
    css.id = "mintagepay-popup-css";
    css.textContent = [
      "html.mp-open,html.mp-open body{overflow:hidden!important}",
      "#mintagepay-popup{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(8,12,16,.72)}",
      "#mintagepay-popup .mp-box{--mp-bg:#fff;--mp-ink:#14181c;--mp-muted:#5b6670;--mp-line:#e3e7ea;display:flex;flex-direction:column;width:min(480px,100%);height:min(800px,100%);overflow:hidden;border-radius:16px;background:var(--mp-bg);color:var(--mp-ink);box-shadow:0 24px 64px rgba(0,0,0,.45);font:14px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;letter-spacing:normal;text-transform:none}",
      "@media (prefers-color-scheme:dark){#mintagepay-popup .mp-box{--mp-bg:#0f1316;--mp-ink:#eef2f4;--mp-muted:#9aa6af;--mp-line:#232a30}}",
      "#mintagepay-popup .mp-head{flex:none;display:flex;align-items:center;gap:8px;padding:12px 10px 12px 16px;border-bottom:1px solid var(--mp-line)}",
      "#mintagepay-popup .mp-lock{display:inline-flex;color:#16a34a}",
      "#mintagepay-popup .mp-title{font-weight:600}",
      "#mintagepay-popup button{font:inherit;cursor:pointer;border-radius:999px;padding:8px 14px;line-height:1.2;text-transform:none;letter-spacing:normal}",
      "#mintagepay-popup .mp-close{margin-left:auto;border:0;background:transparent;color:var(--mp-muted)}",
      "#mintagepay-popup .mp-close:hover,#mintagepay-popup .mp-close:focus-visible{background:rgba(127,127,127,.14);color:var(--mp-ink)}",
      "#mintagepay-popup .mp-body{position:relative;flex:1;min-height:0}",
      "#mintagepay-popup iframe{display:block;width:100%;height:100%;border:0;background:transparent;opacity:0;transition:opacity .2s}",
      "#mintagepay-popup.is-ready iframe{opacity:1}",
      "#mintagepay-popup .mp-loading{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;color:var(--mp-muted)}",
      "#mintagepay-popup.is-ready .mp-loading{display:none}",
      "#mintagepay-popup .mp-spin{width:28px;height:28px;border-radius:50%;border:3px solid var(--mp-line);border-top-color:var(--mp-muted);animation:mp-spin .8s linear infinite}",
      "@keyframes mp-spin{to{transform:rotate(360deg)}}",
      "#mintagepay-popup .mp-confirm{position:absolute;left:12px;right:12px;top:12px;padding:16px;border:1px solid var(--mp-line);border-radius:12px;background:var(--mp-bg);box-shadow:0 12px 32px rgba(0,0,0,.25)}",
      "#mintagepay-popup .mp-confirm[hidden]{display:none}",
      "#mintagepay-popup .mp-confirm p{margin:0 0 12px}",
      "#mintagepay-popup .mp-actions{display:flex;gap:8px;flex-wrap:wrap}",
      "#mintagepay-popup .mp-stay{border:0;background:var(--mp-ink);color:var(--mp-bg)}",
      "#mintagepay-popup .mp-leave{border:1px solid var(--mp-line);background:transparent;color:var(--mp-ink)}",
      "@media (max-width:560px){#mintagepay-popup{padding:0}#mintagepay-popup .mp-box{width:100%;height:100%;border-radius:0}}",
    ].join("\n");
    document.head.appendChild(css);
  }

  const el = (tag, cls, text) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  };
  const btn = (cls, text) => {
    const b = el("button", cls, text);
    b.type = "button";
    return b;
  };

  const overlay = el("div", "");
  overlay.id = "mintagepay-popup";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Secure payment");
  const box = el("div", "mp-box");
  const head = el("div", "mp-head");
  const lock = el("span", "mp-lock");
  lock.setAttribute("aria-hidden", "true");
  lock.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';
  const close = btn("mp-close", "Close");
  head.append(lock, el("span", "mp-title", "Secure payment"), close);
  const body = el("div", "mp-body");
  const loading = el("div", "mp-loading");
  loading.append(el("span", "mp-spin"), el("span", "", "Opening the secure payment page…"));
  const frame = document.createElement("iframe");
  frame.title = "Secure payment";
  // camera: Stripe's photo ID check (when Stripe asks for one before a card payment) can use the webcam.
  frame.setAttribute("allow", "clipboard-write; payment; camera");
  frame.src = url;
  const confirm = el("div", "mp-confirm");
  confirm.hidden = true;
  const actions = el("div", "mp-actions");
  const stay = btn("mp-stay", "Keep paying");
  const leave = btn("mp-leave", "Leave");
  actions.append(stay, leave);
  confirm.append(
    el(
      "p",
      "",
      "Leave without paying? If you have already sent the money, stay on this screen until it shows as received.",
    ),
    actions,
  );
  body.append(loading, frame, confirm);
  box.append(head, body);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
  document.documentElement.classList.add("mp-open");

  let stage = "choose";
  let ready = false;
  let leaving = false;
  const before = document.activeElement;
  const timers = [];

  // The payment page did not come up in the frame (blocked, offline): the usual redirect.
  const fallback = () => {
    if (!ready && !leaving) {
      leaving = true;
      window.location.href = url;
    }
  };
  // Back to the page that shows the order's new status.
  const goReturn = () => {
    if (!leaving) {
      leaving = true;
      window.location.assign(returnPath ?? window.location.pathname);
    }
  };
  const onMessage = (e) => {
    if (e.origin !== origin || e.source !== frame.contentWindow) return;
    const d = e.data;
    if (!d || typeof d !== "object" || d.source !== "mintagepay" || d.invoice !== invoice) return;
    if (d.type === "ready" || d.type === "stage") {
      ready = true;
      overlay.classList.add("is-ready");
    }
    if (d.type === "ready" && buyer) {
      try {
        frame.contentWindow?.postMessage({ source: "mintagepay-store", v: 1, type: "buyer", invoice, buyer }, origin);
      } catch {
        /* the frame went away */
      }
    }
    if (d.type === "stage" && typeof d.stage === "string") {
      stage = d.stage;
      // Let the buyer see the tick, then the account page.
      if (stage === "paid") timers.push(window.setTimeout(goReturn, 2200));
    } else if (d.type === "return") {
      goReturn();
    } else if (d.type === "close") {
      teardown(true);
    }
  };
  const onKey = (e) => {
    if (e.key === "Escape" || e.key === "Esc") {
      e.preventDefault();
      requestClose();
    }
  };
  // Keep keyboard focus inside the pop-up while it is open.
  const onFocus = (e) => {
    if (!overlay.contains(e.target)) close.focus();
  };
  // Tell Mintage Pay the buyer closed the pop-up, and at which step (fire and forget, never in the way).
  const reportClosed = () => {
    if (!ready || typeof fetch !== "function") return;
    const code = /^[a-z_]{1,20}$/.test(stage) ? stage : "choose";
    fetch(`${origin}/api/checkout/${encodeURIComponent(invoice)}/events`, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ event: "popup_closed", code }),
    }).catch(() => undefined);
  };
  function teardown(closedByBuyer) {
    if (closedByBuyer) reportClosed();
    timers.forEach((t) => window.clearTimeout(t));
    window.removeEventListener("message", onMessage);
    document.removeEventListener("keydown", onKey);
    document.removeEventListener("focusin", onFocus);
    overlay.remove();
    document.documentElement.classList.remove("mp-open");
    try {
      before?.focus();
    } catch {
      /* gone */
    }
  }
  function requestClose() {
    if (stage === "received" || stage === "paid") {
      goReturn();
      return;
    }
    if (stage === "send" && confirm.hidden) {
      // The address is on screen: the buyer may already have sent money.
      confirm.hidden = false;
      stay.focus();
      return;
    }
    teardown(true);
  }

  window.addEventListener("message", onMessage);
  document.addEventListener("keydown", onKey);
  document.addEventListener("focusin", onFocus);
  close.addEventListener("click", requestClose);
  stay.addEventListener("click", () => {
    confirm.hidden = true;
    close.focus();
  });
  leave.addEventListener("click", () => teardown(true));
  frame.addEventListener("load", () => {
    if (!ready) timers.push(window.setTimeout(fallback, 6000));
  });
  timers.push(window.setTimeout(fallback, 15000));
  close.focus();
}
